import React, { useMemo, useState, useEffect, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Lazy load all persistent tab components to avoid circular dependencies
const Dashboard = lazy(() => import('../../pages/Dashboard/Dashboard'));
const Collection = lazy(() => import('../../pages/Collection/Collection'));
const SoldInventory = lazy(() => import('../../pages/SoldInventory/SoldInventory'));
const MasterSets = lazy(() => import('../../pages/Sets/MasterSets'));
const SetDetails = lazy(() => import('../../pages/SetDetails/SetDetails'));
const OnePieceSetDetails = lazy(() => import('../../pages/OnePieceSetDetails/OnePieceSetDetails'));
const Grading = lazy(() => import('../../pages/Grading/Grading'));
const TcgDrops = lazy(() => import('../../pages/InventoryTracker/TcgDrops'));
const TradeHistory = lazy(() => import('../../pages/Trades/TradeHistory'));
const SearchPage = lazy(() => import('../../pages/UnifiedSearch/UnifiedSearch'));
const OnePieceDeckBuilder = lazy(() => import('../../pages/OnePieceDeckBuilder/OnePieceDeckBuilder'));
const BuyList = lazy(() => import('../../pages/BuyList/BuyList'));
const PackOpening = lazy(() => import('../../pages/PackOpening/PackOpening'));

const MainTabSwitcher: React.FC = () => {
  const location = useLocation();

  // Tab configurations
  const tabConfigs = useMemo(() => [
    { id: 'dashboard', paths: ['/dashboard'] },
    { id: 'search', paths: ['/search'] },
    { id: 'collection', paths: ['/collection'] },
    { id: 'packs', paths: ['/packs'] },
    { id: 'sold', paths: ['/sold'] },
    { id: 'drops', paths: ['/drops'] },
    { id: 'deck-builder', paths: ['/onepiece/deck-builder'] },
    { id: 'master-sets', paths: ['/master-sets', '/sets', '/onepiece/sets'] },
    { id: 'grading', paths: ['/grading'] },
    { id: 'trades', paths: ['/trades/history'] },
    { id: 'buy-list', paths: ['/buy-list'] },
  ], []);

  // Determine which tab is currently active
  const activeTabId = useMemo(() => {
    const activeTab = tabConfigs.find(tab => 
      tab.paths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'))
    );
    return activeTab?.id;
  }, [location.pathname, tabConfigs]);

  // Keep track of which tabs have been visited
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (activeTabId && !visitedTabs.has(activeTabId)) {
      setVisitedTabs(prev => new Set(prev).add(activeTabId));
    }
  }, [activeTabId, visitedTabs]);

  // Sub-path flags for Master Sets
  const isMasterRoot = location.pathname === '/master-sets' || location.pathname === '/sets' || location.pathname === '/onepiece/sets';
  const isOnePieceSetDetail = location.pathname.startsWith('/onepiece/sets/') && !location.pathname.includes('/cards/');
  const isPokemonSetDetail = location.pathname.startsWith('/sets/') && !location.pathname.includes('/cards/') && location.pathname !== '/sets';

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      }>
        {visitedTabs.has('dashboard') && (
          <Box sx={{ display: activeTabId === 'dashboard' ? 'block' : 'none' }}>
            <Dashboard />
          </Box>
        )}
        
        {visitedTabs.has('search') && (
          <Box sx={{ display: activeTabId === 'search' ? 'block' : 'none' }}>
            <SearchPage />
          </Box>
        )}

        {visitedTabs.has('collection') && (
          <Box sx={{ display: activeTabId === 'collection' ? 'block' : 'none' }}>
            <Collection />
          </Box>
        )}

        {visitedTabs.has('packs') && (
          <Box sx={{ display: activeTabId === 'packs' ? 'block' : 'none' }}>
            <PackOpening />
          </Box>
        )}

        {visitedTabs.has('sold') && (
          <Box sx={{ display: activeTabId === 'sold' ? 'block' : 'none' }}>
            <SoldInventory />
          </Box>
        )}

        {visitedTabs.has('drops') && (
          <Box sx={{ display: activeTabId === 'drops' ? 'block' : 'none' }}>
            <TcgDrops />
          </Box>
        )}

        {visitedTabs.has('deck-builder') && (
          <Box sx={{ display: activeTabId === 'deck-builder' ? 'block' : 'none' }}>
            <OnePieceDeckBuilder />
          </Box>
        )}

        {visitedTabs.has('master-sets') && (
          <Box sx={{ display: activeTabId === 'master-sets' ? 'block' : 'none' }}>
            <Box sx={{ display: isMasterRoot ? 'block' : 'none' }}>
              <MasterSets />
            </Box>
            <Box sx={{ display: isOnePieceSetDetail ? 'block' : 'none' }}>
              <OnePieceSetDetails />
            </Box>
            <Box sx={{ display: isPokemonSetDetail ? 'block' : 'none' }}>
              <SetDetails />
            </Box>
          </Box>
        )}

        {visitedTabs.has('grading') && (
          <Box sx={{ display: activeTabId === 'grading' ? 'block' : 'none' }}>
            <Grading />
          </Box>
        )}

        {visitedTabs.has('trades') && (
          <Box sx={{ display: activeTabId === 'trades' ? 'block' : 'none' }}>
            <TradeHistory />
          </Box>
        )}

        {visitedTabs.has('buy-list') && (
          <Box sx={{ display: activeTabId === 'buy-list' ? 'block' : 'none' }}>
            <BuyList />
          </Box>
        )}
      </Suspense>
    </Box>
  );
};

export default MainTabSwitcher;
